<template>
    <div
        v-qa="'pagination'"
        class="pagination"
    >
        <ul class="list">
            <li class="arrow">
                <!-- TODO translate -->
                <ButtonRound
                    v-qa="'previous-page'"
                    aria-label="Go to previous page"
                    :to="getRoutePage && getRoutePage(previous)"
                    :disabled="current === 1"
                    icon="angle-left"
                    @click="onClick"
                />
            </li>

            <li
                v-for="(page, index) in list"
                :key="index"
                class="item"
            >
                <!-- TODO translate -->
                <ButtonRound
                    v-qa="`page-${page}`"
                    class="button-round"
                    :aria-label="`Go to page ${page}`"
                    :to="getRoutePage && getRoutePage(page)"
                    :disabled="page === current"
                    :type="current === page ? 'bordered' : undefined"
                    @click="onClick"
                >
                    {{ page }}
                </ButtonRound>
            </li>

            <li class="arrow">
                <!-- TODO translate -->
                <ButtonRound
                    v-qa="'next-page'"
                    aria-label="Go to next page"
                    :to="getRoutePage && getRoutePage(next)"
                    :disabled="current === pageCount"
                    icon="angle-right"
                    @click="onClick"
                />
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { range as Range } from 'remeda'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type { ScrollIntoViewOffset } from '@/views/product/composables/ui/useScrollIntoView'
import { useScrollIntoView } from '@/views/product/composables/ui/useScrollIntoView'

import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'

interface Props {
    current: number
    count: number
    limit: number
    range: number
    maxPage?: number
    getRoutePage: (page: number) => ButtonRouteRelative
    scrollIntoViewSelector?: string
    scrollIntoViewOffset?: ScrollIntoViewOffset
}

const {
    count,
    current,
    limit,
    range,
    maxPage = undefined,
    getRoutePage,
    scrollIntoViewSelector = '',
    scrollIntoViewOffset = undefined,
} = defineProps<Props>()

const pageCount = computed(() => {
    const c = Math.ceil(count / limit)

    if (maxPage && c > maxPage) {
        return maxPage
    }

    return c || 1
})

const previous = computed(() => Math.max(1, current - 1))

const next = computed(() => Math.min(current + 1, pageCount.value))

const list = computed(() => [
    ...Range(
        Math.max(1, current - range),
        Math.min(pageCount.value, current + range) + 1
    ),
])

const { scrollIntoView } = useScrollIntoView(
    scrollIntoViewSelector,
    scrollIntoViewOffset
)

const onClick = () => {
    if (scrollIntoViewSelector) {
        scrollIntoView()
    }
}
</script>

<style lang="scss" scoped>
.pagination {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto 60px;
    padding: 0 10px;
    background-color: $white;

    @include min-width(360) {
        padding: 0 20px;
    }

    .list {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;

        &.arrow {
            &:first-child {
                margin-right: auto;

                @include min-width(768) {
                    margin-right: initial;
                }
            }

            &:last-child {
                margin-left: auto;

                @include min-width(768) {
                    margin-left: initial;
                }
            }
        }

        .item {
            margin: 0;

            @include min-width(1024) {
                margin: 0 10px;
            }

            .button-round {
                width: 40px;
                height: 40px;
                background: transparent;
                color: var(--slate);
                font-size: 1rem;
                line-height: 40px;

                &.disabled {
                    color: $blue;
                }
            }

            .router-link-active {
                border: 1px solid $blue;
                line-height: 38px;
            }
        }
    }
}
</style>
